import {
  requestMeetingMinutesFromAudio,
  updateMeeting,
  uploadMeetingAudio,
} from '@/services/meetings'
import { ApiError } from '@/services/api'
import { getServiceStrings } from '@/services/i18n'
import type {
  MeetingUpdateResult,
  TranscriptSegment,
  UpdateMeetingPayload,
} from '@ai-meeting/shared'
import { withRetry } from '@/utils/withRetry'
import { buildTitleSuffixCandidate } from './resolveMeetingTitleForSave'

/**
 * Metadata persisted onto the meeting row that was created up-front at Start.
 *
 * All fields are optional on the wire (they map to `UpdateMeetingDto`), but
 * callers should provide transcript + duration + translationSegments to
 * preserve a synchronous client snapshot. For native meetings the backend
 * may replace the source transcript with its reconciled projection after the
 * snapshot is stored; translation segments continue to use this payload.
 *
 * Carries no `audio` — the recording is uploaded separately by
 * {@link uploadMeetingAudioInBackground} — and no `completed`, which this module
 * owns. Both used to be declared here and then ignored, which is why callers had
 * to pass them anyway to satisfy the type.
 */
export interface FinalizeMeetingPayload {
  transcript?: TranscriptSegment[]
  translationSegments?: TranscriptSegment[]
  durationSeconds: number
  title?: string
  feedbackRating?: number
  feedbackComment?: string
  realtimeContext?: string
  minutesContext?: string
  insights?: UpdateMeetingPayload['insights']
  liveMemo?: UpdateMeetingPayload['liveMemo']
  assistCards?: UpdateMeetingPayload['assistCards']
}

const TITLE_CONFLICT_CODES = new Set(['MEETING_TITLE_TAKEN', 'MEETING_TITLE_TAKEN_IN_TRASH'])
const MAX_TITLE_SAVE_ATTEMPTS = 10

function isMeetingTitleConflict(error: unknown): boolean {
  return error instanceof ApiError && TITLE_CONFLICT_CODES.has(error.code ?? '')
}

export type FinalizeMeetingPayloadWithoutTitle = Omit<FinalizeMeetingPayload, 'title'>

/**
 * Persists metadata and sets the meeting title, retrying with ` (2)`, ` (3)`, …
 * when the account already owns that name.
 */
export async function completeMeetingMetadataWithTitle(
  meetingId: string,
  payload: FinalizeMeetingPayloadWithoutTitle,
  options: { title: string }
): Promise<MeetingUpdateResult> {
  const baseTitle = options.title
  let lastError: unknown
  for (let attempt = 0; attempt < MAX_TITLE_SAVE_ATTEMPTS; attempt++) {
    const candidate = buildTitleSuffixCandidate(baseTitle, attempt)
    try {
      return await updateMeeting(meetingId, { ...payload, completed: true, title: candidate })
    } catch (error) {
      if (!isMeetingTitleConflict(error)) throw error
      lastError = error
    }
  }
  throw lastError
}

const AUDIO_UPLOAD_ATTEMPTS = 2
const AUDIO_UPLOAD_RETRY_DELAY_MS = 750

/**
 * Persist the meeting metadata (transcript, translation segments, duration,
 * feedback, insights) to the meeting row already created up-front. Awaiting
 * this resolves quickly so the UI can navigate to the meeting detail page.
 *
 * @param meetingId Backend UUID for the meeting row created on modal submit.
 * @param payload   Metadata to persist. The recording itself is uploaded
 *   separately, by {@link uploadMeetingAudioInBackground}.
 * @returns The updated meeting (normalized), without `transcript`/
 *   `translationSegments` — see {@link MeetingUpdateResult}.
 */
export async function completeMeetingMetadata(
  meetingId: string,
  payload: FinalizeMeetingPayload
): Promise<MeetingUpdateResult> {
  const { title, ...rest } = payload
  // The row itself was created back on Start, so without this flag an abandoned
  // session and a finished one are indistinguishable. History and the minutes
  // screen read it to label the ones that never finished (BRAI-413).
  if (title !== undefined) {
    return completeMeetingMetadataWithTitle(meetingId, rest, { title })
  }
  return updateMeeting(meetingId, { ...rest, completed: true })
}

/**
 * Upload the captured audio blob to the saved meeting with retry. Designed to
 * be fired-and-forgotten after the user has already navigated to the detail
 * page; surface errors via the global save toast instead of blocking the UI.
 *
 * @param meetingId Backend id for the meeting row.
 * @param audio     Archived audio blob; pass `null` when none was captured.
 * @throws Re-throws the last upload error after exhausting retries.
 */
export async function uploadMeetingAudioInBackground(
  meetingId: string,
  audio: Blob | null
): Promise<void> {
  // Logged rather than returned silently. Nothing is wrong with a meeting that
  // captured no audio, but the server is never told either way — the row keeps
  // its default `audioUploadStatus: 'NONE'` — so without this line "there was
  // nothing to send" and "the recorder came back empty when it should not
  // have" look identical afterwards, and only one of them is a bug.
  if (!audio || audio.size === 0) {
    console.warn(
      `Meeting ${meetingId}: no audio to upload (${audio ? 'blob is empty' : 'no blob captured'})`
    )
    return
  }
  await withRetry(() => uploadMeetingAudio(meetingId, audio), {
    attempts: AUDIO_UPLOAD_ATTEMPTS,
    delayMs: AUDIO_UPLOAD_RETRY_DELAY_MS,
    fallbackMessage: getServiceStrings().failedUploadMeetingAudio,
  })
}

/**
 * Ask the backend to generate minutes once the recording has been stored,
 * without blocking End Meeting navigation.
 *
 * Takes no blob: the request is only the meeting id. Call it after the upload
 * request is accepted; the backend owns the remaining S3 wait and returns 202
 * even while `audioUploadStatus` is still `PENDING`.
 */
export async function requestMeetingMinutesFromAudioInBackground(
  meetingId: string
): Promise<void> {
  await withRetry(() => requestMeetingMinutesFromAudio(meetingId), {
    attempts: AUDIO_UPLOAD_ATTEMPTS,
    delayMs: AUDIO_UPLOAD_RETRY_DELAY_MS,
    fallbackMessage: getServiceStrings().failedGenerateMeetingMinutes,
  })
}
